import { Box, Typography } from '@mui/material';
import { JSX } from 'react';
import { IAppointment } from '../../../library/models/appointment';
import { DayColumn } from './DayColumn';
import {
    HOUR_HEIGHT,
    LABEL_COLUMN,
    gridStyles,
    hourLabelStyles,
    hourSlotStyles,
    laneRowStyles,
    nowLineStyles,
    pastShadeStyles,
} from './myCalendar.styles';

interface ScheduleGridProps {
    /** One ISO date for a day, seven for a week. Everything else is derived from its length. */
    days: string[];
    appointmentsByDay: Record<string, IAppointment[]>;
    firstHour: number;
    lastHour: number;
    /** Today's ISO date, so the grid can tell past columns from future ones. */
    today: string;
    /** How far down the grid the current moment falls, in pixels. */
    nowOffset: number;
    /** Whether the moment lands on the grid at all -- see the caller for why it might not. */
    showsNow: boolean;
    isPast: (appointment: IAppointment) => boolean;
    isUnresolved: (appointment: IAppointment) => boolean;
    onSelect: (appointment: IAppointment) => void;
    /** An empty half hour was clicked, as the wall clock a new entry would start at. */
    onCreateAt: (startsAt: string) => void;
}

/**
 * The time grid: an hour axis down the left and one column per day beside it.
 *
 * One component draws both views. A day is this with a single column, and the only thing
 * that changes is how many there are -- which is the whole reason the week view was
 * affordable at all.
 *
 * The weekday names are not here. They are `DayHeaderRow`, mounted by the page in a
 * sticky rail above this, so that they survive the page being scrolled -- but they share
 * this grid's column template, so the two still line up.
 */
export const ScheduleGrid = ({
    days,
    appointmentsByDay,
    firstHour,
    lastHour,
    today,
    nowOffset,
    showsNow,
    isPast,
    isUnresolved,
    onSelect,
    onCreateAt,
}: ScheduleGridProps): JSX.Element => {
    const isWeek: boolean = days.length > 1;

    const hours: number[] = Array.from(
        { length: Math.max(lastHour - firstHour, 1) },
        (_, index) => firstHour + index
    );

    const gridHeight: number = hours.length * HOUR_HEIGHT;

    /*
     * A column's place on the grid, as CSS rather than as a number.
     *
     * Nothing here knows what a column is worth in pixels -- the template is `1fr`-based
     * so that seven of them share whatever room there is -- so the shade and the now-line
     * are positioned with a `calc()` against the grid's own width instead. A day view
     * spans the whole thing, hour gutter included, which is what it has always shaded.
     */
    const columnLeft = (index: number): string =>
        isWeek
            ? `calc(${LABEL_COLUMN}px + (100% - ${LABEL_COLUMN}px) * ${index} / ${days.length})`
            : '0px';

    const columnWidth: string = isWeek
        ? `calc((100% - ${LABEL_COLUMN}px) / ${days.length})`
        : '100%';

    /**
     * How much of a column is already spent.
     *
     * A day gone by is spent to the bottom, today down to the line, and a day still to
     * come not at all. Paging back used to show no shade whatever, which read as if a past
     * Tuesday were still ahead of the therapist.
     */
    const shadeHeight = (day: string): number => {
        if (day < today) {
            return gridHeight;
        }

        return day === today ? Math.min(Math.max(nowOffset, 0), gridHeight) : 0;
    };

    const todayIndex: number = days.indexOf(today);

    return (
        <Box sx={gridStyles(days.length)}>
            {hours.map(hour => (
                <Box key={hour} sx={{ display: 'contents' }}>
                    <Typography sx={hourLabelStyles}>
                        {String(hour).padStart(2, '0')}:00
                    </Typography>

                    {days.map((day, index) => (
                        <Box key={day} sx={hourSlotStyles(index > 0)} />
                    ))}
                </Box>
            ))}

            {/* Decoration, and hidden from a screen reader: every card already carries
                its own time and status as text, so a line and a wash add nothing that
                is not already spoken. Order matters -- the shades must precede the
                lanes to paint under them, and the line must follow to paint over. */}
            {days.map((day, index) =>
                shadeHeight(day) > 0 ? (
                    <Box
                        key={day}
                        aria-hidden
                        data-testid="my-calendar-past-shade"
                        data-day={day}
                        sx={pastShadeStyles(shadeHeight(day), columnLeft(index), columnWidth)}
                    />
                ) : null
            )}

            <Box sx={laneRowStyles}>
                {days.map(day => (
                    <DayColumn
                        key={day}
                        day={day}
                        appointments={appointmentsByDay[day] ?? []}
                        firstHour={firstHour}
                        lastHour={lastHour}
                        isPast={isPast}
                        isUnresolved={isUnresolved}
                        onSelect={onSelect}
                        onCreateAt={onCreateAt}
                        density={isWeek ? 'week' : 'day'}
                    />
                ))}
            </Box>

            {showsNow && todayIndex >= 0 && (
                <Box
                    aria-hidden
                    data-testid="my-calendar-now-line"
                    sx={nowLineStyles(
                        nowOffset,
                        columnLeft(todayIndex),
                        columnWidth,
                        // The dot marks where the lane starts: past the hour gutter on
                        // a day, and on the column's own edge in a week.
                        isWeek ? -4 : LABEL_COLUMN - 4
                    )}
                />
            )}
        </Box>
    );
};
